import React, { useState, useContext } from "react";
import PropTypes from "prop-types";
import cx from "classnames";

import type {
  ReactDefaultPropsWithChildren,
  ReactComponentStaticProps,
} from "../../types/react";
import { useUniqueId } from "../hooks/useUniqueId";
import { ThemeContext } from "../Theme";
import { DropdownInput } from "../DropdownInput";

import styles from "./DropdownList.scss";

export const DropdownListTagName = "div";
export type DropdownListDefaultTag = typeof DropdownListTagName;

export type DropdownListItem = React.ElementType<{
  item: string;
  label?: string;
  selected: boolean;
  disabled: boolean;
  selectHandler: (selected: boolean) => void;
}>;

export type DropdownListStaticContent = React.ElementType<{
  maxReached: boolean;
  value: string[];
  validate: (value: string[]) => boolean;
  itemClass: string;
  focusClass: string;
  setValue: (value: string[]) => void;
  setIsValid: (isValid: boolean) => void;
}>;

export type DropdownListBaseProps<
  Tag extends React.ElementType = DropdownListDefaultTag
> = {
  tag?: Tag;
  itemComponent: DropdownListItem;
  staticContent?: DropdownListStaticContent;
  customClass?: string;
  contentCustomClass?: string;
  listCustomClass?: string;
  listItemCustomClass?: string;
  id?: string;
  items: string[];
  itemLabels?: (string | null | undefined)[];
  value?: string[];
  updateValue?: (
    value: string[],
    items: string[],
    item: string,
    selected: boolean
  ) => string[];
  closeOnUpdate?: boolean;
  prefix?: string;
  isValid?: boolean;
  onChange?: (selectedItem: string[]) => void;
  afterOpen?: (selectedItems: string[] | undefined) => void;
  afterClose?: (selectedItems: string[] | undefined) => void;
  isOpen?: boolean;
  minSelectionCount?: number;
  maxSelectionCount?: number;
  valueRenderer?: (prefix: string, value: string[] | undefined) => string;
};

const defaultProps = {
  tag: DropdownListTagName,
  children: "",
  staticContent: undefined,
  customClass: undefined,
  contentCustomClass: undefined,
  listCustomClass: undefined,
  listItemCustomClass: undefined,
  id: undefined,
  value: [] as string[],
  itemLabels: undefined,
  updateValue: (
    value: string[],
    items: string[],
    item: string,
    selected: boolean
  ) => {
    const newValue = [...value];
    if (selected) newValue.push(item);
    else newValue.splice(newValue.indexOf(item), 1);
    return newValue.sort((a, b) => items.indexOf(a) - items.indexOf(b));
  },
  closeOnUpdate: false,
  prefix: "",
  isValid: undefined,
  onChange: undefined,
  afterOpen: undefined,
  afterClose: undefined,
  isOpen: undefined,
  minSelectionCount: 0,
  maxSelectionCount: undefined,
  valueRenderer: (prefix: string, value: string[] | undefined) =>
    value && value.length > 0 ? `${prefix} (${value.length})` : "",
} as const;

const validatedDefaultProps: ReactDefaultPropsWithChildren<
  DropdownListBaseProps,
  typeof defaultProps
> = defaultProps;

const propTypes = {
  tag: PropTypes.elementType,
  itemComponent: PropTypes.elementType.isRequired,
  staticContent: PropTypes.elementType,
  customClass: PropTypes.string,
  contentCustomClass: PropTypes.string,
  listCustomClass: PropTypes.string,
  listItemCustomClass: PropTypes.string,
  id: PropTypes.string,
  items: PropTypes.arrayOf(PropTypes.string.isRequired).isRequired,
  itemLabels: PropTypes.arrayOf(PropTypes.string),
  value: PropTypes.arrayOf(PropTypes.string.isRequired),
  updateValue: PropTypes.func,
  closeOnUpdate: PropTypes.bool,
  prefix: PropTypes.string,
  isValid: PropTypes.bool,
  onChange: PropTypes.func,
  afterOpen: PropTypes.func,
  afterClose: PropTypes.func,
  isOpen: PropTypes.bool,
  minSelectionCount: PropTypes.number,
  maxSelectionCount: PropTypes.number,
  valueRenderer: PropTypes.func,
  children: PropTypes.oneOfType([
    PropTypes.node,
    PropTypes.arrayOf(PropTypes.node),
  ]),
} as const;

export type DropdownListProps<
  Tag extends React.ElementType = DropdownListDefaultTag
> = DropdownListBaseProps<Tag> &
  Omit<React.ComponentPropsWithRef<Tag>, keyof DropdownListBaseProps<Tag>>;

export type DropdownListPropsWithoutRef<
  Tag extends React.ElementType = DropdownListDefaultTag
> = DropdownListBaseProps<Tag> &
  Omit<React.ComponentPropsWithoutRef<Tag>, keyof DropdownListBaseProps<Tag>>;

const DropdownListWithRef = <
  Tag extends React.ElementType = DropdownListDefaultTag
>(
  props: DropdownListPropsWithoutRef<Tag>,
  ref: React.ComponentPropsWithRef<Tag>["ref"]
): JSX.Element => {
  const {
    tag = defaultProps.tag,
    itemComponent: ItemComponent,
    staticContent: StaticContent,
    customClass = defaultProps.customClass,
    contentCustomClass = defaultProps.contentCustomClass,
    listCustomClass = defaultProps.listCustomClass,
    listItemCustomClass = defaultProps.listItemCustomClass,
    id = defaultProps.id,
    items,
    itemLabels = defaultProps.itemLabels,
    value: controlledValue = defaultProps.value,
    updateValue = defaultProps.updateValue,
    closeOnUpdate = defaultProps.closeOnUpdate,
    prefix = defaultProps.prefix,
    isValid: controlledIsValid = defaultProps.isValid,
    children,
    onChange = defaultProps.onChange,
    minSelectionCount = defaultProps.minSelectionCount,
    maxSelectionCount = defaultProps.maxSelectionCount,
    valueRenderer = defaultProps.valueRenderer,
    afterOpen = defaultProps.afterOpen,
    afterClose = defaultProps.afterClose,
    isOpen: controlledIsOpen = defaultProps.isOpen,
    ...restProps
  } = props;

  const validate = (testValue: string[]) =>
    testValue.length >= minSelectionCount &&
    (typeof maxSelectionCount === "undefined" ||
      testValue.length <= maxSelectionCount);

  const [lastControlledIsOpen, setLastControlledIsOpen] = useState(
    controlledIsOpen
  );
  const [isOpen, setIsOpen] = useState(controlledIsOpen);
  if (controlledIsOpen !== lastControlledIsOpen) {
    setLastControlledIsOpen(controlledIsOpen);
    setIsOpen(controlledIsOpen);
  }
  const [lastControlledIsValid, setLastControlledIsValid] = useState(
    controlledIsValid
  );
  const [isValid, setIsValid] = useState(
    typeof controlledIsValid !== "undefined"
      ? controlledIsValid
      : validate(controlledValue)
  );
  if (controlledIsValid !== lastControlledIsValid) {
    setLastControlledIsValid(controlledIsValid);
    if (typeof controlledIsValid !== "undefined") setIsValid(controlledIsValid);
  }
  const [lastControlledValue, setLastControlledValue] = useState(
    controlledValue
  );
  const [value, setValue] = useState(controlledValue);
  if (controlledValue !== lastControlledValue) {
    setLastControlledValue(controlledValue);
    setValue(controlledValue);
    setIsValid(validate(controlledValue));
  }

  const maxReached = value.length >= (maxSelectionCount || Infinity);

  const dropdownMultiSelectId =
    id || `dropdown-multi-select-${useUniqueId("DropdownList", props) + 1}`;

  const theme = useContext(ThemeContext);

  const handleAfterOpen = () => {
    if (afterOpen) afterOpen(value);
  };

  const handleAfterClose = () => {
    if (afterClose) {
      // Getting synchronized current value for use in callback
      setValue((prev) => {
        afterClose(prev);
        return prev;
      });
    }
  };

  const themeClass = theme && styles[theme];

  const setDropdownListValue = (
    newValue: string[],
    dropdownInputContext: React.ContextType<typeof DropdownInput.Context>
  ) => {
    const { setValue: setDropdownInputValue, close } = dropdownInputContext;
    setDropdownInputValue(valueRenderer(prefix, newValue));
    setValue(newValue);
    setIsValid(validate(newValue));
    if (onChange) onChange(newValue);
    if (closeOnUpdate)
      setTimeout(() => {
        requestAnimationFrame(() => close());
      }, 75);
  };

  const staticContent = StaticContent && (
    <DropdownInput.Context.Consumer>
      {(dropdownInputContext) => {
        const { itemClass, focusClass } = dropdownInputContext;
        return (
          <StaticContent
            value={value}
            validate={validate}
            maxReached={maxReached}
            setIsValid={setIsValid}
            setValue={(newValue: string[]) =>
              setDropdownListValue(newValue, dropdownInputContext)
            }
            itemClass={itemClass}
            focusClass={focusClass}
          />
        );
      }}
    </DropdownInput.Context.Consumer>
  );

  return (
    <DropdownInput
      ref={ref}
      tag={tag as "div"}
      contentTag="div"
      id={dropdownMultiSelectId}
      label={children}
      value={valueRenderer(prefix, value)}
      isValid={isValid}
      customClass={cx(customClass, styles.wrapper)}
      contentCustomClass={cx(contentCustomClass, styles.content)}
      staticContent={staticContent || undefined}
      afterOpen={handleAfterOpen}
      afterClose={handleAfterClose}
      isOpen={isOpen}
      {...restProps}
    >
      <ul className={cx(themeClass, listCustomClass, styles.list)}>
        <DropdownInput.Context.Consumer>
          {(dropdownInputContext) => {
            const { itemClass, focusClass } = dropdownInputContext;

            const itemValueLabelPairs: {
              value: string;
              label: string | undefined | null;
            }[] = [];
            for (let i = 0; i < items.length; i += 1) {
              itemValueLabelPairs.push({
                value: items[i],
                label: itemLabels && itemLabels[i],
              });
            }

            return itemValueLabelPairs.map((item) => {
              const { value: itemValue, label } = item;
              const itemChecked = value.indexOf(itemValue) >= 0;
              const itemDisabled =
                !itemChecked && value.indexOf(itemValue) < 0 && maxReached;
              const itemChange = (checked: boolean) => {
                const newValue = updateValue(value, items, itemValue, checked);
                setDropdownListValue(newValue, dropdownInputContext);
              };

              return (
                <li key={`${dropdownMultiSelectId}-${itemValue}`}>
                  <button
                    type="button"
                    tabIndex={-1}
                    onClick={(event) => {
                      event.preventDefault();
                      itemChange(!itemChecked);
                    }}
                    onKeyPress={(event) => {
                      event.preventDefault();
                    }}
                    className={cx(
                      styles["list-item"],
                      itemClass,
                      focusClass,
                      {
                        [styles["list-item--disabled"]]:
                          value.indexOf(itemValue) < 0 && maxReached,
                      },
                      listItemCustomClass
                    )}
                  >
                    <ItemComponent
                      item={itemValue}
                      label={label || itemValue}
                      selected={itemChecked}
                      disabled={itemDisabled}
                      selectHandler={(checked) => itemChange(checked)}
                      tabIndex={-1}
                    />
                  </button>
                </li>
              );
            });
          }}
        </DropdownInput.Context.Consumer>
      </ul>
    </DropdownInput>
  );
};

export const DropdownList = (React.forwardRef(
  DropdownListWithRef
) as unknown) as (<Tag extends React.ElementType = DropdownListDefaultTag>(
  props: DropdownListProps<Tag>
) => ReturnType<typeof DropdownListWithRef>) &
  ReactComponentStaticProps<
    DropdownListProps,
    "tag" | "staticContent" | "itemComponent"
  >;

DropdownList.displayName = "DropdownList";

DropdownList.propTypes = propTypes;

DropdownList.defaultProps = validatedDefaultProps;

export default DropdownList;
